import { fetchVariantData as resolveVariant, prefetchVariantData as prefetchVariant, normalizeVariantId } from '@utilities/product';

function setButtonLoadingState(button, isLoading) {
  if (!button) return;

  if (isLoading) {
    button.setAttribute('disabled', '');
  } else {
    button.removeAttribute('disabled');
  }
}

function closeClosestQuickview(element) {
  const quickview = element?.closest?.('quickview-modal');
  if (quickview) quickview.close();
}

async function addItemsToCart(items) {
  const {addToCartViaStandardAction} = await import('@utilities/theme');
  return addToCartViaStandardAction(items);
}

async function submitLookbookCart({
  button,
  sourceElement,
  items,
  onError
}) {
  setButtonLoadingState(button, true);

  try {
    await addItemsToCart(items);
    closeClosestQuickview(sourceElement);
    return true;
  } catch (error) {
    if (typeof onError === 'function') onError(error);
    return false;
  } finally {
    setButtonLoadingState(button, false);
  }
}

if (!customElements.get('lookbook-product-card')) {
  class LookbookProductCard extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.boundHandleOptionChange = this.handleOptionChange.bind(this);
      this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      this.state = this.createInitialState();
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    init() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.productUrl = this.dataset.productUrl;
      this.priceElement = this.querySelector('[data-lookbook-price]');
      this.button = this.querySelector('[data-lookbook-add-single]');
      this.notifyButton = this.querySelector('.listrak-notify-button');
      this.form = this.querySelector('.product__form');
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.productInput = this.form?.querySelector('input[name="product"]');
      this.variantSkuInput = this.form?.querySelector('input[name="variant_sku"]');
      this.preorderIcon = this.querySelector('.js-preorder-icon');
      this.preorderText = this.querySelector('.js-preorder-text');
      this.optionGroups = Array.from(this.querySelectorAll('[data-lookbook-option-group], .js-product-radios'));

      this.state = {
        ...this.createInitialState(),
        selectedVariantId: normalizeVariantId(this.variantInput?.value),
        optionValueIds: this.getSelectedOptionValueIds(),
        available: this.button ? !this.button.hasAttribute('disabled') : true
      };

      this.initOptionListeners();
      this.initForm();
      this.syncListrakVariantDataFromState();
      this.syncNotifyButtonVisibility(this.state.available, {noVariant: !this.state.selectedVariantId});
      this.initOptionAvailabilityState();
    }

    async initOptionAvailabilityState() {
      if (!this.productUrl) return;
      const optionValueIds = this.getSelectedOptionValueIds();

      try {
        const data = await this.fetchVariantData(optionValueIds);
        this.applyVariantData(data, optionValueIds);
      } catch (error) {
        console.error('Lookbook Product: Initial availability sync failed:', error);
      }
    }

    createInitialState() {
      return {
        selectedVariantId: null,
        optionValueIds: [],
        available: true,
        priceHtml: '',
        showPreorderMessage: false,
        optionsData: null
      };
    }

    initOptionListeners() {
      for (const group of this.optionGroups) {
        const radios = group.querySelectorAll('input[type="radio"]');
        for (const radio of radios) {
          radio.addEventListener('change', this.boundHandleOptionChange, {
            signal: this.abortController?.signal
          });
        }
      }
    }

    initForm() {
      if (!this.form) return;

      this.form.addEventListener('submit', this.boundHandleFormSubmit, {
        signal: this.abortController?.signal
      });
    }

    async handleFormSubmit(event) {
      event.preventDefault();
      const currentForm = event.currentTarget;
      if (!(currentForm instanceof HTMLElement)) return;

      const item = this.toCartItem('lookbook-product');
      if (!item) return;

      await submitLookbookCart({
        button: this.button,
        sourceElement: currentForm,
        items: item,
        onError: (error) => {
          console.error('Lookbook Product: Error adding to cart:', error);
        }
      });
    }

    getSelectedOptionValueIds() {
      const groups = this.getSortedOptionGroups();

      const ids = [];
      for (const group of groups) {
        const checked = group.querySelector('input[type="radio"]:checked');
        const id = this.getOptionValueId(checked);
        if (id) ids.push(id);
      }

      return ids;
    }

    getSortedOptionGroups() {
      return [...this.optionGroups].sort((a, b) => this.getOptionGroupIndex(a) - this.getOptionGroupIndex(b));
    }

    getOptionGroupIndex(group) {
      const key = group?.dataset?.optionKey || group?.dataset?.jsOptionIndex || '1';
      const parsed = Number.parseInt(String(key).replace('option', ''), 10);
      return Number.isFinite(parsed) ? parsed : 1;
    }

    getOptionValueId(input) {
      if (!(input instanceof HTMLElement)) return '';
      return String(input.dataset.lookbookOptionValueId || input.dataset.optionValueId || '').trim();
    }

    async fetchVariantData(optionValueIds = []) {
      return resolveVariant(this.productUrl, optionValueIds);
    }

    async handleOptionChange(event) {
      const optionValueIds = this.getSelectedOptionValueIds();
      const data = await this.fetchVariantData(optionValueIds);
      const selectedVariant = this.applyVariantData(data, optionValueIds);
      if (!selectedVariant) return;
      this.prefetchLikelyVariants(event?.currentTarget, optionValueIds);
    }

    applyVariantData(data, optionValueIds = []) {
      const selectedVariant = data?.selected_variant;
      const optionsData = data?.options;

      if (!selectedVariant) {
        this.state = {
          ...this.state,
          selectedVariantId: null,
          optionValueIds,
          available: false,
          optionsData: optionsData || null
        };
        if (this.state.optionsData) this.renderOptionAvailability(this.state.optionsData);
        this.renderButtonState(this.state.available, {noVariant: true});
        return null;
      }

      const selectedVariantId = normalizeVariantId(selectedVariant.id);
      if (!selectedVariantId) {
        this.renderButtonState(false, {noVariant: true});
        return null;
      }

      this.state = {
        ...this.state,
        selectedVariantId,
        optionValueIds,
        available: Boolean(selectedVariant.available),
        priceHtml: selectedVariant.price_html || '',
        showPreorderMessage: Boolean(selectedVariant.show_preorder_message),
        optionsData: optionsData || null
      };

      if (this.variantInput && this.state.selectedVariantId) {
        this.variantInput.value = String(this.state.selectedVariantId);
      }
      this.syncListrakVariantData(selectedVariant);

      this.renderPrice(this.state.priceHtml);
      this.renderPreorderInfo(this.state.showPreorderMessage);
      if (this.state.optionsData) this.renderOptionAvailability(this.state.optionsData);
      this.renderButtonState(this.state.available, {noVariant: false});
      return selectedVariant;
    }

    syncListrakVariantData(selectedVariant) {
      if (!selectedVariant || typeof selectedVariant !== 'object') return;

      const productId = selectedVariant.product_id ?? this.dataset.productId ?? '';
      const variantId = selectedVariant.id ?? this.state.selectedVariantId ?? '';
      const variantSku = selectedVariant.sku ?? '';

      this.dataset.productId = productId ? String(productId) : '';
      this.dataset.variantId = variantId ? String(variantId) : '';
      this.dataset.variantSku = variantSku ? String(variantSku) : '';

      if (this.productInput) this.productInput.value = this.dataset.productId;
      if (this.variantInput) this.variantInput.value = this.dataset.variantId;
      if (this.variantSkuInput) this.variantSkuInput.value = this.dataset.variantSku;
    }

    syncListrakVariantDataFromState() {
      const productId = this.dataset.productId || this.productInput?.value || '';
      const variantId = this.dataset.variantId || this.variantInput?.value || '';
      const variantSku = this.dataset.variantSku || this.variantSkuInput?.value || '';

      this.dataset.productId = productId ? String(productId) : '';
      this.dataset.variantId = variantId ? String(variantId) : '';
      this.dataset.variantSku = variantSku ? String(variantSku) : '';

      if (this.productInput) this.productInput.value = this.dataset.productId;
      if (this.variantInput) this.variantInput.value = this.dataset.variantId;
      if (this.variantSkuInput) this.variantSkuInput.value = this.dataset.variantSku;
    }

    prefetchLikelyVariants(changedInput, optionValueIds = []) {
      if (!(changedInput instanceof HTMLElement)) return;
      const changedGroup = changedInput.closest('[data-lookbook-option-group], .js-product-radios');
      if (!changedGroup) return;

      const candidateIds = this.getPrefetchCandidateIds(changedGroup, optionValueIds);
      for (const candidate of candidateIds) {
        prefetchVariant(this.productUrl, candidate);
      }
    }

    getPrefetchCandidateIds(changedGroup, optionValueIds = []) {
      const selectedIdInGroup = this.getOptionValueId(changedGroup.querySelector('input[type="radio"]:checked'));
      const baseIds = optionValueIds.filter(id => id && id !== selectedIdInGroup);
      const inputs = changedGroup.querySelectorAll('input[type="radio"]');
      const candidates = [];
      const seen = new Set();

      for (const input of inputs) {
        if (!(input instanceof HTMLInputElement)) continue;
        if (input.checked) continue;
        if (input.classList.contains('impossible-combination')) continue;
        const optionValueId = this.getOptionValueId(input);
        if (!optionValueId || seen.has(optionValueId)) continue;
        seen.add(optionValueId);
        candidates.push([...baseIds, optionValueId]);
        if (candidates.length >= 2) break;
      }

      return candidates;
    }

    renderOptionAvailability(optionsData) {
      if (!optionsData || !Array.isArray(optionsData)) return;

      for (const option of optionsData) {
        if (!option.values || !Array.isArray(option.values)) continue;

        for (const value of option.values) {
          const optionValueId = String(value.id);
          const matchingInputs = this.querySelectorAll(
            `input[data-lookbook-option-value-id="${optionValueId}"], input[data-option-value-id="${optionValueId}"]`
          );

          for (const input of matchingInputs) {
            const isImpossible = value.variant === null;
            const isUnavailable = value.available === false;
            this.setOptionInputAvailabilityState(input, {isImpossible, isUnavailable});
          }
        }
      }
    }

    setOptionInputAvailabilityState(input, {isImpossible = false, isUnavailable = false} = {}) {
      if (!(input instanceof HTMLInputElement)) return;

      input.classList.remove('impossible-combination', 'option-unavailable', 'disabled-radio');
      input.removeAttribute('disabled');
      input.removeAttribute('tabindex');
      input.removeAttribute('aria-disabled');

      if (isImpossible) {
        input.classList.add('impossible-combination');
        input.setAttribute('disabled', '');
        input.setAttribute('tabindex', '-1');
        input.setAttribute('aria-disabled', 'true');
        return;
      }

      if (isUnavailable) {
        input.classList.add('option-unavailable');
      }
    }

    syncNotifyButtonVisibility(isEnabled, {noVariant = false} = {}) {
      if (!this.notifyButton) return;
      const shouldShowNotify = !isEnabled && !noVariant;
      this.notifyButton.style.display = shouldShowNotify ? '' : 'none';
      this.notifyButton.setAttribute('aria-hidden', shouldShowNotify ? 'false' : 'true');
    }

    renderButtonState(isEnabled, {noVariant = false} = {}) {
      if (!this.button) return;

      if (isEnabled) {
        this.button.innerText = this.button.getAttribute('data-js-default-text');
        setButtonLoadingState(this.button, false);
      } else {
        this.button.innerText = this.button.getAttribute('data-js-disabled-text');
        setButtonLoadingState(this.button, true);
      }
      this.button.classList.toggle('no-variant', Boolean(noVariant));
      this.syncNotifyButtonVisibility(isEnabled, {noVariant});
    }

    renderPrice(priceHtml) {
      if (!priceHtml || !this.priceElement) return;
      this.priceElement.innerHTML = priceHtml;
    }

    renderPreorderInfo(showPreorder) {
      const shouldHide = !showPreorder;
      if (this.preorderIcon) this.preorderIcon.classList.toggle('hidden', shouldHide);
      if (this.preorderText) this.preorderText.classList.toggle('hidden', shouldHide);
    }

    collectFormProperties() {
      const properties = {};
      if (!this.form) return properties;

      const propertyInputs = this.form.querySelectorAll('input[type="hidden"][name^="properties["]');
      for (const el of propertyInputs) {
        const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
        if (propertyName) properties[propertyName] = el.value;
      }

      return properties;
    }

    toCartItem(source = 'lookbook-product') {
      const variantId = normalizeVariantId(this.variantInput?.value || this.state.selectedVariantId);
      if (!variantId) return null;
      const properties = this.collectFormProperties();
      properties._add_to_cart_component = source;
      return {
        id: variantId,
        quantity: 1,
        properties
      };
    }

    getSelectedProductInfo() {
      return this.toCartItem('lookbook-product');
    }
  }

  customElements.define('lookbook-product-card', LookbookProductCard);
}

class LookbookShopController {
  constructor(root) {
    this.root = root;
    this.abortController = null;
    this.slider = null;
    this.sliderInitAttempts = 0;
    this.sliderListeners = [];
  }

  init() {
    this.abortController = new AbortController();
    this.root.style.display = 'block';
    this.points = this.root.querySelectorAll('[data-lookbook-point]');
    this.sliderEl = this.root.querySelector('embla-slider.lookbook-dropdown__slides');
    this.lookbookProducts = this.root.querySelectorAll('[data-lookbook-product]');
    this.lookbookSliderWrapper = this.root.querySelector('[data-lookbook-slider-wrapper]');
    this.lookbookImage = this.root.querySelector('[data-lookbook-image]');
    this.lookbookDropdownClose = this.root.querySelector('[data-lookbook-dropdown-close]');

    this.initSlider();
    this.initPointers();
    this.initAddAllProducts();
    this.initMobileSliderTriggers();
  }

  initMobileSliderTriggers() {
    if (!this.lookbookImage || !this.lookbookSliderWrapper || !this.lookbookDropdownClose) return;

    this.lookbookImage.addEventListener('click', event => {
      if (window.innerWidth < 990) {
        const image = event.currentTarget;
        if (!(image instanceof HTMLElement)) return;

        const lookbook = image.closest('.lookbook');
        if (!lookbook) return;

        const currentActiveSliderWrapper = lookbook.querySelector('[data-lookbook-slider-wrapper].is-active');
        const currentActiveDropdown = lookbook.querySelector('[data-lookbook-dropdown].is-open');

        if (this.lookbookSliderWrapper.classList.contains('is-active')) return;

        if (currentActiveSliderWrapper) {
          currentActiveSliderWrapper.classList.remove('is-active');
          currentActiveDropdown?.classList.remove('is-open');
        }
        this.lookbookSliderWrapper.classList.add('is-active');
        image.closest('[data-lookbook-dropdown]')?.classList.add('is-open');
        this.refreshSlider();
      }
    }, {signal: this.abortController?.signal});

    this.lookbookDropdownClose.addEventListener('click', () => {
      this.lookbookSliderWrapper.classList.remove('is-active');
      this.root.closest('[data-lookbook-dropdown]')?.classList.remove('is-open');
    }, {signal: this.abortController?.signal});
  }

  async addAllProducts(button) {
    const lookbookItems = this.collectLookbookItems();

    if (lookbookItems.length === 0) {
      this.showCartError('No products available to add from this look.');
      return;
    }

    await submitLookbookCart({
      button,
      sourceElement: this.root,
      items: lookbookItems,
      onError: error => {
        this.showCartError(error?.message || 'Unable to add items to your bag.');
      }
    });
  }

  showCartError(message) {
    const errorContainer = this.root.querySelector('[data-lookbook-cart-error]');
    if (!errorContainer) return;

    errorContainer.textContent = message;
    errorContainer.removeAttribute('hidden');

    setTimeout(() => {
      errorContainer.setAttribute('hidden', '');
    }, 8000);
  }

  collectLookbookItems() {
    const requestBodyItems = [];

    this.lookbookProducts.forEach(element => {
      const item = element.toCartItem?.('lookbook-shop') || element.getSelectedProductInfo?.();
      if (!item) return;

      const normalizedVariantId = normalizeVariantId(item.id);
      const quantity = Number.parseInt(item.quantity, 10);
      if (!normalizedVariantId || !Number.isInteger(quantity) || quantity <= 0) return;

      item.id = normalizedVariantId;
      if (!item.properties || typeof item.properties !== 'object') item.properties = {};
      item.properties._add_to_cart_component = 'lookbook-shop';
      requestBodyItems.push(item);
    });

    return requestBodyItems;
  }

  initAddAllProducts() {
    this.root.querySelectorAll('[data-lookbook-add-all]').forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        const currentButton = event.currentTarget;
        if (!(currentButton instanceof HTMLElement)) return;
        this.addAllProducts(currentButton);
      }, {signal: this.abortController?.signal});
    });
  }

  initPointers() {
    if (!this.points) return;
    this.points.forEach(element => element.addEventListener('click', () => {
      const index = Number(element.dataset.index);
      if (!Number.isFinite(index)) return;
      this.slider?.scrollTo(index);
    }, {signal: this.abortController?.signal}));
  }

  refreshSlider() {
    if (!this.sliderEl) return;
    if (this.sliderEl.classList.contains('embla--inactive') || this.sliderEl.hasAttribute('data-embla-disabled')) {
      return;
    }
    if (typeof this.sliderEl.refresh === 'function') {
      this.sliderEl.refresh();
    }
    this.sliderInitAttempts = 0;
    this.initSlider();
  }

  initSlider() {
    if (this.root.hasAttribute('disabled') || !this.sliderEl) {
      return;
    }
    if (this.sliderEl.classList.contains('embla--inactive') || this.sliderEl.hasAttribute('data-embla-disabled')) {
      return;
    }

    if (typeof this.sliderEl.refresh === 'function') {
      this.sliderEl.refresh();
    }

    const emblaApi = this.sliderEl.emblaApi;
    if (!emblaApi) {
      if (this.sliderInitAttempts < 10) {
        this.sliderInitAttempts += 1;
        window.requestAnimationFrame(() => this.initSlider());
      }
      return;
    }

    this.slider = emblaApi;
    this.syncSliderHeight();

    const onSelect = () => this.syncSliderHeight();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    this.sliderListeners.push({event: 'select', callback: onSelect});
    this.sliderListeners.push({event: 'reInit', callback: onSelect});
  }

  syncSliderHeight() {
    if (!this.slider || !this.sliderEl) return;
    const viewport = this.sliderEl.querySelector('.embla__viewport');
    const slideNodes = this.slider.slideNodes?.() || [];
    const selectedIndex = this.slider.selectedScrollSnap?.() ?? 0;
    const selectedSlide = slideNodes[selectedIndex];
    if (!viewport || !selectedSlide) return;

    const targetHeight = selectedSlide.clientHeight;
    if (targetHeight > 0) {
      this.root.style.height = `${targetHeight}px`;
      viewport.style.height = `${targetHeight + 2}px`;
    }
  }

  destroy() {
    this.abortController?.abort();
    if (this.slider) {
      this.sliderListeners.forEach(({event, callback}) => {
        this.slider.off?.(event, callback);
      });
      this.slider = null;
      this.sliderListeners = [];
    }
  }
}

if (!customElements.get('lookbook-grid')) {
  class LookbookGrid extends HTMLElement {
    constructor() {
      super();
      this.dropdownTriggers = this.querySelectorAll('[data-lookbook-trigger]');
      this.dropdowns = this.querySelectorAll('[data-lookbook-dropdown]');
      this.dropdownCloseBtns = this.querySelectorAll('[data-lookbook-close]');
      this.dropdownIdPairs = {};
      this.scrollOffset = 95;
      this.scrollDelayMs = 200;
      this.abortController = null;

      this.dropdowns.forEach(dropdown => {
        const dropdownId = dropdown.dataset.id;
        if (dropdownId) {
          const item = dropdown.closest('.lookbook')?.querySelector(`.lookbook-item--${dropdownId}`);
          this.dropdownIdPairs[dropdownId] = {dropdown, item};
        }
      });

      this.currentActiveDropdown = null;
      this.currentActiveItem = null;
      this.shopControllers = [];
    }

    disconnectActivePair() {
      if (!this.currentActiveDropdown || !this.currentActiveItem) return;

      this.currentActiveDropdown.classList.remove('is-active');
      this.currentActiveItem.classList.remove('is-selected');
      this.currentActiveItem.closest('.lookbook-grid__body')?.classList.remove('is-dropdown-active');
      this.currentActiveDropdown = null;
      this.currentActiveItem = null;
    }

    closePair(dropdown, item) {
      if (!dropdown || !item) return;
      dropdown.classList.remove('is-active');
      item.classList.remove('is-selected');
      item.closest('.lookbook-grid__body')?.classList.remove('is-dropdown-active');
      this.currentActiveDropdown = null;
      this.currentActiveItem = null;
    }

    handleCloseTriggers() {
      this.dropdownCloseBtns.forEach(element => {
        element.addEventListener('click', event => {
          event.preventDefault();
          const closeButton = event.currentTarget;
          if (!(closeButton instanceof HTMLElement)) return;
          const dropdown = closeButton.closest('[data-lookbook-dropdown]');
          const pair = dropdown ? this.dropdownIdPairs[dropdown.dataset.id] : null;
          this.closePair(pair?.dropdown, pair?.item);
        }, {signal: this.abortController?.signal});
      });
    }

    scrollToDropdown(dropdown) {
      setTimeout(() => {
        const offsetTop = dropdown.offsetTop;
        window.scrollTo({
          top: offsetTop - this.scrollOffset,
          behavior: 'smooth'
        });
      }, this.scrollDelayMs);
    }

    activatePair(selectedDropdown, selectedItem, row) {
      if (!selectedDropdown || !selectedItem) return;

      this.disconnectActivePair();
      selectedItem.closest('.lookbook-grid__body')?.classList.add('is-dropdown-active');
      selectedItem.classList.add('is-selected');
      selectedDropdown.closest('[data-lookbook-body]')?.style.setProperty('grid-row', String(row + 2));
      selectedDropdown.classList.add('is-active');
      const radio = selectedDropdown.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      this.currentActiveDropdown = selectedDropdown;
      this.currentActiveItem = selectedItem;

      const shopController = selectedDropdown.querySelector('[data-lookbook-shop]')?.__lookbookShopController;
      shopController?.refreshSlider();
      this.scrollToDropdown(selectedDropdown);
    }

    getTriggerData(trigger) {
      const {id: blockId, index, columns} = trigger.dataset;
      return {
        blockId,
        index: Number(index),
        columns: Number(columns)
      };
    }

    isValidTriggerData(triggerData) {
      return Boolean(
        triggerData.blockId &&
        Number.isFinite(triggerData.index) &&
        Number.isFinite(triggerData.columns) &&
        triggerData.columns > 0
      );
    }

    handleDropdownTriggerClick(event) {
      event.preventDefault();

      const trigger = event.currentTarget;
      if (!(trigger instanceof HTMLElement)) return;

      const triggerData = this.getTriggerData(trigger);
      if (!this.isValidTriggerData(triggerData)) return;

      const row = Math.floor(triggerData.index / triggerData.columns);
      const selectedPair = this.dropdownIdPairs[triggerData.blockId];
      this.activatePair(selectedPair?.dropdown, selectedPair?.item, row);
    }

    handleDropdownTriggers() {
      this.dropdownTriggers.forEach(element => {
        element.addEventListener('click', event => this.handleDropdownTriggerClick(event), {
          signal: this.abortController?.signal
        });
      });
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.initShopControllers();
      this.handleDropdownTriggers();
      this.handleCloseTriggers();
    }

    initShopControllers() {
      this.querySelectorAll('[data-lookbook-shop]').forEach(element => {
        if (element.__lookbookShopController) return;
        const controller = new LookbookShopController(element);
        controller.init();
        element.__lookbookShopController = controller;
        this.shopControllers.push(controller);
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.shopControllers.forEach(controller => controller.destroy());
      this.shopControllers = [];
    }
  }

  customElements.define('lookbook-grid', LookbookGrid);
}
